// Kasse — eine Fläche, kein Vorab-Schritt.
//
// Früher: erst Kunde wählen, dann Artikel suchen (zwölf Treffer), dann unten
// rechts Zahlart, „bezahlt?", Rückgeld. Laufkundschaft musste jedes Mal
// „Als Barverkauf kassieren" anklicken, bevor überhaupt ein Artikel ging.
//
// Jetzt:
//   links    Suchen oder scannen (Handscanner tippt einfach los, Kamera wo
//            möglich), darunter „Häufig verkauft" und die Kategorien als Kacheln.
//            Freier Betrag für alles ohne Artikel (Dienstleistung, Pfand).
//   rechts   der Bon. Mehrere Bons parallel („parken"), Kunde nur bei Bedarf,
//            Zeile antippen für Menge, Preis, Rabatt; Rabatt auf den ganzen Bon.
//   Bezahlen ein Fenster: Bar mit Schnellbeträgen und Rückgeld, Karte, geteilt
//            (bar + Karte), auf Rechnung mit Anzahlung.
//
// Der Verkauf bleibt eine Rechnung und bucht den Bestand ab — das Backend
// erledigt beides in einem Schritt.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { useDaten, euro, menge as mengeText, profil } from '../../../gemeinsam/lib/daten';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { meldungZeigen } from '../../../gemeinsam/komponenten/Meldungen';
import { Hinweis, Karte, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';
import Icon from '../../../gemeinsam/komponenten/Icon';
import type { Artikel, Kunde } from '../../../gemeinsam/lib/typen';
import { einheitAnzeige, istTeilbar } from '../../artikel/lib/einheiten';
import {
  brutto, einzelNetto, leererBon, nettoAusBrutto, neueId, nochMoeglich, standLesen, standSchreiben, summen, zeilenBrutto,
  type Bon, type Stand, type Zeile,
} from '../lib/kasse';
import { Bezahlen, FreierBetrag, KundeWaehlen, zahlEingabe } from '../komponenten/KasseFenster';
import { KameraScanner, kameraVerfuegbar, useScannerTastatur } from '../komponenten/KasseScanner';

const HAEUFIG = '__haeufig';
const ALLE = '__alle';
const RABATTE = [5, 10, 15, 20];

export default function Kasse() {
  const { t } = useSprache();
  const { benutzer, mandant } = useAuth();
  const { daten, laedt, neuLaden: artikelNeu } = useDaten<{ produkte: Artikel[] }>('/products');
  const { daten: kundenDaten } = useDaten<Kunde[]>('/customers');
  const { daten: favoriten } = useDaten<Array<{ produktId: string; verkaeufe: number }>>('/invoices/kasse/favoriten');
  const kunden = kundenDaten ?? [];

  // Bons: im Browser dieses Geräts, je Benutzer und Betrieb.
  const speicher = `lp_kasse_${mandant?.id ?? ''}_${benutzer?.id ?? ''}`;
  const [stand, setStandRoh] = useState<Stand>(() => standLesen(speicher));
  const setStand = (f: (s: Stand) => Stand) => setStandRoh(s => { const n = f(s); standSchreiben(speicher, n); return n; });
  const bon = stand.bons.find(b => b.id === stand.aktiv) ?? stand.bons[0];
  const aendereBon = (f: (b: Bon) => Bon) => setStand(s => ({ ...s, bons: s.bons.map(b => b.id === bon.id ? f(b) : b) }));

  const [suche, setSuche] = useState('');
  const [reiter, setReiter] = useState<string>(HAEUFIG);
  const [offeneZeile, setOffeneZeile] = useState<string | null>(null);
  const [fenster, setFenster] = useState<null | 'bezahlen' | 'kunde' | 'frei' | 'kamera'>(null);
  const [warnung, setWarnung] = useState<string | null>(null);
  const [bonRabatt, setBonRabatt] = useState(false);
  const suchfeld = useRef<HTMLInputElement>(null);

  const artikel = useMemo(() => (daten?.produkte ?? []).filter(p => p.aktiv !== false), [daten]);
  const kategorien = useMemo(() => [...new Set(artikel.map(p => p.kategorie).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [artikel]);
  const haeufig = useMemo(() => {
    const nachId = new Map(artikel.map(p => [p.id, p]));
    return (favoriten ?? []).map(f => nachId.get(f.produktId)).filter((p): p is Artikel => !!p).slice(0, 18);
  }, [artikel, favoriten]);
  // Ohne Verkaufshistorie gibt es nichts „Häufiges" — dann gleich alle zeigen.
  const reiterWirklich = reiter === HAEUFIG && haeufig.length === 0 ? ALLE : reiter;

  const kacheln = useMemo(() => {
    const s = suche.trim().toLowerCase();
    if (s) {
      return artikel.filter(p => p.name.toLowerCase().includes(s) || (p.artikelnummer || '').toLowerCase().includes(s) || (p.ean || '').toLowerCase() === s).slice(0, 48);
    }
    if (reiterWirklich === HAEUFIG) return haeufig;
    if (reiterWirklich === ALLE) return artikel.slice(0, 120);
    return artikel.filter(p => p.kategorie === reiterWirklich);
  }, [artikel, suche, reiterWirklich, haeufig]);

  const summe = summen(bon);
  const kunde = kunden.find(k => k.id === bon.kundeId) ?? null;

  useEffect(() => { if (!warnung) return; const id = setTimeout(() => setWarnung(null), 4000); return () => clearTimeout(id); }, [warnung]);

  // ── Bon bearbeiten ─────────────────────────────────────────────────────────

  function hinzufuegen(p: Artikel, anzahl = 1) {
    const frei = nochMoeglich(bon, p.id, Number(p.bestand) || 0);
    if (frei <= 0) {
      setWarnung((Number(p.bestand) || 0) <= 0
        ? t('kasse.nichtAufLager').replace('{name}', p.name)
        : t('kasse.nurNoch').replace('{name}', p.name).replace('{menge}', mengeText(p.bestand, einheitAnzeige(p.einheit, t))));
      return;
    }
    const teilbar = istTeilbar(p.einheit);
    const dazu = teilbar ? Math.min(anzahl, frei) : Math.min(anzahl, Math.floor(frei)) || 0;
    if (!dazu) return;
    aendereBon(b => {
      const vorhanden = b.zeilen.find(z => z.produktId === p.id && !z.rabatt && z.preis === p.verkaufspreis);
      if (vorhanden && !teilbar) {
        return { ...b, zeilen: b.zeilen.map(z => z === vorhanden ? { ...z, menge: z.menge + dazu } : z) };
      }
      const z: Zeile = {
        id: neueId(), produktId: p.id, bezeichnung: p.name, einheit: p.einheit, menge: dazu,
        preis: p.verkaufspreis, steuersatz: p.steuersatz ?? profil().standardSteuersatz, rabatt: 0, teilbar, bestand: Number(p.bestand) || 0,
      };
      // Gewogene Ware: Zeile gleich öffnen, damit das Gewicht eingetippt wird.
      if (teilbar) setOffeneZeile(z.id);
      return { ...b, zeilen: [...b.zeilen, z] };
    });
  }

  function zeileAendern(id: string, f: (z: Zeile) => Zeile | null) {
    aendereBon(b => ({ ...b, zeilen: b.zeilen.flatMap(z => {
      if (z.id !== id) return [z];
      const neu = f(z);
      return neu ? [neu] : [];
    }) }));
  }

  function mengeSetzen(z: Zeile, neu: number) {
    if (neu <= 0) { zeileAendern(z.id, () => null); return; }
    if (z.produktId && z.bestand !== null) {
      const andere = bon.zeilen.filter(x => x.produktId === z.produktId && x.id !== z.id).reduce((s, x) => s + x.menge, 0);
      const p = artikel.find(a => a.id === z.produktId);
      const max = (Number(p?.bestand ?? z.bestand) || 0) - andere;
      if (neu > max) { setWarnung(t('kasse.nurNoch').replace('{name}', z.bezeichnung).replace('{menge}', mengeText(max, einheitAnzeige(z.einheit, t)))); return; }
    }
    zeileAendern(z.id, x => ({ ...x, menge: Math.round(neu * 1000) / 1000 }));
  }

  /** Code aus Scanner oder Suchfeld: EAN oder Artikelnummer genau, sonst der einzige Treffer. */
  function codeEinloesen(code: string): boolean {
    const c = code.trim().toLowerCase();
    if (!c) return false;
    const genau = artikel.find(p => (p.ean || '').toLowerCase() === c || (p.artikelnummer || '').toLowerCase() === c);
    if (genau) { hinzufuegen(genau); return true; }
    return false;
  }
  useScannerTastatur(code => { if (!codeEinloesen(code)) setWarnung(t('kasse.codeUnbekannt').replace('{code}', code)); }, fenster === null);

  // ── Bons parken ────────────────────────────────────────────────────────────

  function neuerBon() {
    const b = leererBon();
    setStand(s => ({ aktiv: b.id, bons: [...s.bons, b].slice(-8) }));
    setOffeneZeile(null);
    suchfeld.current?.focus();
  }
  function bonEntfernen(id: string) {
    setStand(s => {
      const rest = s.bons.filter(b => b.id !== id);
      if (!rest.length) { const b = leererBon(); return { aktiv: b.id, bons: [b] }; }
      return { aktiv: s.aktiv === id ? rest[rest.length - 1].id : s.aktiv, bons: rest };
    });
  }
  function verkauft() {
    setFenster(null);
    bonEntfernen(bon.id);
    void artikelNeu(); // Bestand hat sich geändert
    suchfeld.current?.focus();
  }

  if (laedt && !daten) return <div className="flex justify-center py-16"><Spinner /></div>;

  const offen = bon.zeilen.find(z => z.id === offeneZeile) ?? null;

  return (
    <div className="mx-auto max-w-7xl space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('kasse.titel')}</h1>
        <div className="flex gap-2">
          <Knopf onClick={() => setFenster('frei')}><Icon name="plus" groesse={16} />{t('kasse.freierBetrag')}</Knopf>
        </div>
      </div>

      {warnung && <Hinweis art="warnung">{warnung}</Hinweis>}

      <div className="grid items-start gap-4 lg:grid-cols-[1fr_380px]">
        {/* ── Artikel ─────────────────────────────────────────────────── */}
        {/* min-w-0: sonst dehnt die seitlich scrollende Kategorienleiste das Raster auf dem Handy. */}
        <Karte klasse="min-w-0">
          <div className="space-y-3 border-b border-slate-200 p-3 dark:border-slate-800">
            <div className="flex gap-2">
              <div className="relative min-w-0 flex-1">
                <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-slate-400"><Icon name="suche" groesse={18} /></span>
                <input ref={suchfeld} value={suche} autoFocus onChange={e => setSuche(e.target.value)}
                       onKeyDown={e => {
                         if (e.key !== 'Enter') return;
                         e.preventDefault();
                         if (codeEinloesen(suche) || (kacheln.length === 1 && (hinzufuegen(kacheln[0]), true))) setSuche('');
                       }}
                       placeholder={t('kasse.artikelSuche')} aria-label={t('kasse.artikelSuchen')}
                       className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pe-3 ps-10 text-base text-slate-900 focus:border-marke-500 focus:outline-none focus:ring-1 focus:ring-marke-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100" />
              </div>
              {kameraVerfuegbar() && (
                <Knopf onClick={() => setFenster('kamera')} aria-label={t('kasse.scannen')} title={t('kasse.scannen')}>
                  <Icon name="kamera" groesse={18} />
                </Knopf>
              )}
            </div>
            {!suche && (
              <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5">
                {([...(haeufig.length ? [[HAEUFIG, t('kasse.haeufig')]] : []), [ALLE, t('kasse.alle')], ...kategorien.map(k => [k, k])] as Array<[string, string]>).map(([id, name]) => (
                  <button key={id} type="button" onClick={() => setReiter(id)}
                          className={`shrink-0 rounded-full border px-3 py-1 text-sm font-medium transition ${reiterWirklich === id
                            ? 'border-marke-600 bg-marke-600 text-white' : 'border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {kacheln.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
              <p>{artikel.length ? t('kasse.keinArtikel') : t('kasse.keineArtikelAngelegt')}</p>
              <button type="button" onClick={() => setFenster('frei')} className="mt-2 font-medium text-marke-600 hover:underline dark:text-marke-400">{t('kasse.freierBetrag')} →</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 xl:grid-cols-4">
              {kacheln.map(p => {
                const leer = (Number(p.bestand) || 0) <= 0;
                const imBon = bon.zeilen.filter(z => z.produktId === p.id).reduce((s, z) => s + z.menge, 0);
                return (
                  <button key={p.id} type="button" onClick={() => hinzufuegen(p)} disabled={leer}
                          className="relative flex min-h-[5.5rem] flex-col rounded-xl border border-slate-200 bg-white p-3 text-start transition hover:border-marke-400 hover:shadow-sm active:scale-[0.98] disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-marke-600">
                    {imBon > 0 && (
                      <span className="absolute -end-1.5 -top-1.5 min-w-6 rounded-full bg-marke-600 px-1.5 text-center text-xs font-bold leading-6 text-white">
                        {String(imBon).replace('.', ',')}
                      </span>
                    )}
                    <span className="line-clamp-2 text-sm font-medium leading-snug text-slate-900 dark:text-slate-100">{p.name}</span>
                    <span className="mt-auto pt-1.5 text-base font-bold tabular-nums text-marke-700 dark:text-marke-300">
                      {euro(brutto(p.verkaufspreis, p.steuersatz ?? profil().standardSteuersatz))}
                      {istTeilbar(p.einheit) && <span className="text-xs font-normal text-slate-500"> / {einheitAnzeige(p.einheit, t)}</span>}
                    </span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">
                      {leer ? t('kasse.nichtVorraetig') : `${mengeText(p.bestand, einheitAnzeige(p.einheit, t))}`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </Karte>

        {/* ── Bon ─────────────────────────────────────────────────────── */}
        <Karte klasse="h-fit min-w-0 lg:sticky lg:top-4">
          <div id="kasse-bon" className="flex items-center gap-1 overflow-x-auto border-b border-slate-200 p-2 dark:border-slate-800">
            {stand.bons.map((b, i) => {
              const s = summen(b);
              const aktiv = b.id === bon.id;
              return (
                <button key={b.id} type="button" onClick={() => { setStand(x => ({ ...x, aktiv: b.id })); setOffeneZeile(null); }}
                        className={`shrink-0 rounded-lg px-2.5 py-1.5 text-start text-xs transition ${aktiv
                          ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                  <span className="block font-semibold">{t('kasse.bonNr').replace('{n}', String(i + 1))}</span>
                  <span className="block tabular-nums opacity-80">{b.zeilen.length ? euro(s.brutto) : '—'}</span>
                </button>
              );
            })}
            <button type="button" onClick={neuerBon} title={t('kasse.parkenHinweis')}
                    className="ms-auto inline-flex shrink-0 items-center gap-1 rounded-lg border border-dashed border-slate-300 px-2.5 py-2 text-xs font-medium text-slate-600 hover:border-marke-400 hover:text-marke-700 dark:border-slate-700 dark:text-slate-300">
              <Icon name="plus" groesse={14} />{t('kasse.neuerBon')}
            </button>
          </div>

          <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-2.5 dark:border-slate-800">
            <button type="button" onClick={() => setFenster('kunde')} className="flex min-w-0 items-center gap-2 text-sm">
              <Icon name="kunden" groesse={16} />
              <span className={`truncate ${kunde ? 'font-medium text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}`}>
                {kunde ? kunde.name : t('kasse.laufkunde')}
              </span>
            </button>
            {kunde ? (
              <button type="button" onClick={() => aendereBon(b => ({ ...b, kundeId: '' }))} aria-label={t('kasse.kundeEntfernen')} className="rounded p-1 text-slate-400 hover:text-red-600">
                <Icon name="schliessen" groesse={14} />
              </button>
            ) : (
              <button type="button" onClick={() => setFenster('kunde')} className="text-xs font-medium text-marke-600 hover:underline dark:text-marke-400">{t('kasse.kundeZuordnen')}</button>
            )}
          </div>

          {bon.zeilen.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('kasse.leer')}</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('kasse.leerNeu')}</p>
            </div>
          ) : (
            <ul className="max-h-[48vh] divide-y divide-slate-200 overflow-y-auto dark:divide-slate-800">
              {bon.zeilen.map(z => (
                <li key={z.id}>
                  <button type="button" onClick={() => setOffeneZeile(offeneZeile === z.id ? null : z.id)}
                          className={`flex w-full items-center gap-2 px-4 py-2.5 text-start ${offeneZeile === z.id ? 'bg-marke-50/60 dark:bg-marke-950/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`}>
                    <span className="w-10 shrink-0 text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-300">
                      {z.teilbar ? String(z.menge).replace('.', ',') : `${z.menge}×`}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">{z.bezeichnung}</span>
                      <span className="block text-xs text-slate-500 dark:text-slate-400">
                        {euro(brutto(einzelNetto(z), z.steuersatz))}{z.teilbar ? ` / ${einheitAnzeige(z.einheit, t)}` : ''}
                        {z.rabatt > 0 && <span className="ms-1 font-semibold text-emerald-600 dark:text-emerald-400">−{z.rabatt} %</span>}
                      </span>
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">{euro(zeilenBrutto(z))}</span>
                  </button>
                  {offen?.id === z.id && <ZeileBearbeiten z={z} beiMenge={n => mengeSetzen(z, n)} beiAendern={f => zeileAendern(z.id, f)} />}
                </li>
              ))}
            </ul>
          )}

          <div className="space-y-3 border-t border-slate-200 p-4 dark:border-slate-800">
            {bon.zeilen.length > 0 && (
              bonRabatt || bon.rabatt > 0 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{t('kasse.bonRabatt')}</span>
                  {[0, ...RABATTE].map(r => (
                    <button key={r} type="button" onClick={() => { aendereBon(b => ({ ...b, rabatt: r })); if (!r) setBonRabatt(false); }}
                            className={`rounded-full border px-2 py-0.5 text-xs font-medium ${bon.rabatt === r ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 text-slate-700 dark:border-slate-700 dark:text-slate-300'}`}>
                      {r ? `${r} %` : t('kasse.keinRabatt')}
                    </button>
                  ))}
                </div>
              ) : (
                <button type="button" onClick={() => setBonRabatt(true)} className="text-xs font-medium text-marke-600 hover:underline dark:text-marke-400">+ {t('kasse.bonRabatt')}</button>
              )
            )}
            <div className="space-y-0.5 text-sm">
              {summe.steuern.map(s => (
                <div key={s.satz} className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span>{t('kasse.enthaltenSteuer').replace('{satz}', String(s.satz)).replace('{name}', profil().steuerKurz)}</span><span className="tabular-nums">{euro(s.steuer)}</span>
                </div>
              ))}
              {summe.ersparnis > 0 && (
                <div className="flex justify-between text-xs font-medium text-emerald-600 dark:text-emerald-400"><span>{t('kasse.gespart')}</span><span className="tabular-nums">−{euro(summe.ersparnis)}</span></div>
              )}
              <div className="flex items-baseline justify-between pt-1">
                <span className="text-base font-semibold text-slate-900 dark:text-slate-100">{t('feld.gesamt')}</span>
                <span className="text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{euro(summe.brutto)}</span>
              </div>
            </div>
            <Knopf variante="primaer" className="w-full !py-3 !text-base" disabled={!bon.zeilen.length} onClick={() => setFenster('bezahlen')}>
              {t('kasse.bezahlenBetrag').replace('{betrag}', euro(summe.brutto))}
            </Knopf>
            {bon.zeilen.length > 0 && (
              <button type="button" onClick={() => { if (stand.bons.length > 1) bonEntfernen(bon.id); else aendereBon(() => ({ ...leererBon(), id: bon.id })); }}
                      className="w-full text-center text-xs text-slate-500 hover:text-red-600 dark:text-slate-400">
                {t('kasse.bonVerwerfen')}
              </button>
            )}
          </div>
        </Karte>
      </div>

      {/* Handy: Summe und Bezahlen immer in Reichweite, auch wenn der Bon unter den Kacheln liegt. */}
      {bon.zeilen.length > 0 && (
        <div className="sticky bottom-3 z-30 lg:hidden">
          <div className="flex items-center gap-2 rounded-2xl bg-slate-900 p-2 ps-4 text-white shadow-xl dark:bg-slate-100 dark:text-slate-900">
            <button type="button" onClick={() => document.getElementById('kasse-bon')?.scrollIntoView({ behavior: 'smooth' })} className="min-w-0 flex-1 text-start">
              <span className="block text-xs opacity-75">{t('kasse.positionen').replace('{n}', String(bon.zeilen.length))}</span>
              <span className="block text-lg font-bold tabular-nums">{euro(summe.brutto)}</span>
            </button>
            <Knopf variante="primaer" onClick={() => setFenster('bezahlen')}>{t('kasse.bezahlen')}</Knopf>
          </div>
        </div>
      )}

      {fenster === 'bezahlen' && (
        <Bezahlen bon={bon} summe={summe} kunden={kunden} beiSchliessen={() => setFenster(null)} beiFertig={() => { meldungZeigen(t('kasse.abgeschlossen')); verkauft(); }}
                  beiKunde={k => aendereBon(b => ({ ...b, kundeId: k?.id ?? '' }))} />
      )}
      {fenster === 'kunde' && (
        <KundeWaehlen kunden={kunden} beiSchliessen={() => setFenster(null)}
                      beiWahl={k => { aendereBon(b => ({ ...b, kundeId: k?.id ?? '' })); setFenster(null); }} />
      )}
      {fenster === 'frei' && (
        <FreierBetrag beiSchliessen={() => setFenster(null)} beiFertig={z => { aendereBon(b => ({ ...b, zeilen: [...b.zeilen, z] })); setFenster(null); }} />
      )}
      {fenster === 'kamera' && (
        <KameraScanner beiSchliessen={() => setFenster(null)}
                       beiCode={code => { setFenster(null); if (!codeEinloesen(code)) setWarnung(t('kasse.codeUnbekannt').replace('{code}', code)); }} />
      )}
    </div>
  );
}

/** Zeile aufgeklappt: Menge, Preis, Rabatt, entfernen. */
function ZeileBearbeiten({ z, beiMenge, beiAendern }: {
  z: Zeile; beiMenge: (n: number) => void; beiAendern: (f: (z: Zeile) => Zeile | null) => void;
}) {
  const { t } = useSprache();
  const [preisText, setPreisText] = useState('');
  return (
    <div className="space-y-2.5 bg-marke-50/60 px-4 pb-3 pt-1 dark:bg-marke-950/30">
      <div className="flex items-center gap-2">
        <span className="w-16 text-xs font-medium text-slate-600 dark:text-slate-400">{t('feld.menge')}</span>
        {z.teilbar ? (
          <input key={z.menge} defaultValue={String(z.menge).replace('.', ',')} inputMode="decimal" autoFocus
                 aria-label={t('feld.menge')} onFocus={e => e.target.select()}
                 onBlur={e => { const n = zahlEingabe(e.target.value); if (n > 0) beiMenge(n); }}
                 onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                 className="w-24 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-end text-sm tabular-nums dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
        ) : (
          <div className="flex items-center gap-1">
            <Knopf variante="still" className="!px-3 !py-1" onClick={() => beiMenge(z.menge - 1)} aria-label="−">−</Knopf>
            <span className="w-8 text-center text-sm font-semibold tabular-nums">{z.menge}</span>
            <Knopf variante="still" className="!px-3 !py-1" onClick={() => beiMenge(z.menge + 1)} aria-label="+">+</Knopf>
          </div>
        )}
        <button type="button" onClick={() => beiAendern(() => null)} className="ms-auto rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40">
          {t('aktion.entfernen')}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="w-16 text-xs font-medium text-slate-600 dark:text-slate-400">{t('kasse.rabatt')}</span>
        {[0, ...RABATTE].map(r => (
          <button key={r} type="button" onClick={() => beiAendern(x => ({ ...x, rabatt: r }))}
                  className={`rounded-full border px-2 py-0.5 text-xs font-medium ${z.rabatt === r ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 text-slate-700 dark:border-slate-700 dark:text-slate-300'}`}>
            {r ? `${r} %` : '—'}
          </button>
        ))}
      </div>
      <form className="flex items-center gap-2" onSubmit={e => {
        e.preventDefault();
        const b = zahlEingabe(preisText);
        if (b > 0) { beiAendern(x => ({ ...x, preis: nettoAusBrutto(b, x.steuersatz), rabatt: 0 })); setPreisText(''); }
      }}>
        <span className="w-16 text-xs font-medium text-slate-600 dark:text-slate-400">{t('kasse.preis')}</span>
        <input value={preisText} onChange={e => setPreisText(e.target.value)} inputMode="decimal" placeholder={euro(brutto(z.preis, z.steuersatz))}
               aria-label={t('kasse.preisAendern')}
               className="w-28 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-end text-sm tabular-nums dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
        <Knopf type="submit" variante="still" className="!px-2.5 !py-1 !text-xs" disabled={!(zahlEingabe(preisText) > 0)}>{t('kasse.preisAendern')}</Knopf>
      </form>
    </div>
  );
}
